import React, { useState, useEffect, useCallback } from 'react';
import { Receipt, TrendingUp, TrendingDown, ArrowUpDown, ChevronLeft, ChevronRight, Target, Pencil, Check, X, Printer, Banknote } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/utils/api';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const fmt = (n) => (n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FinanceDashboard = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [editingObj, setEditingObj] = useState(false);
  const [objValue, setObjValue] = useState('');
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/finance/overview`, { params: { year } });
      setData(res.data);
      setObjValue(String(res.data.objectif_mensuel || 0));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveObjectif = async () => {
    try {
      await axios.put(`${API}/finance/objectif`, { amount: parseFloat(objValue) || 0 });
      toast.success('Objectif mis a jour');
      setEditingObj(false);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  if (loading || !data) return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 text-center text-slate-400">{t('common.loading')}</div>
    </AdminLayout>
  );

  const { factures_etablies: rev, factures_fournisseurs: exp, charges_fixes: gen, tva, objectif_mensuel: obj, current_month_revenue: curRev, monthly } = data;
  const totalExpenses = exp.total_ttc + (gen?.total || 0);
  const objPct = obj > 0 ? Math.min((curRev / obj) * 100, 100) : 0;
  const maxBar = Math.max(...monthly.map(m => Math.max(m.revenue.ttc, m.total_charges || m.expenses.ttc)), 1);

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto" data-testid="finance-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="finance-title">Finances</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()} className="print:hidden" data-testid="print-finance-btn"><Printer className="w-4 h-4 mr-1" /> Imprimer</Button>
            <Button variant="outline" size="sm" onClick={() => setYear(y => y - 1)} data-testid="prev-year"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-bold text-slate-700 min-w-[50px] text-center" data-testid="year-display">{year}</span>
            <Button variant="outline" size="sm" onClick={() => setYear(y => y + 1)} data-testid="next-year"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Comparatif Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Factures etablies */}
          <Card className="p-5 border-l-4 border-l-emerald-500" data-testid="card-revenue">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Factures etablies</h3>
                <p className="text-xs text-slate-500">{rev.count} facture(s) en {year}</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total HT</span>
                <span className="font-medium">CHF {fmt(rev.total_ht)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">TVA collectee</span>
                <span className="font-medium text-emerald-600">CHF {fmt(rev.tva)}</span>
              </div>
              <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-2 mt-1">
                <span>Total TTC</span>
                <span className="text-emerald-700">CHF {fmt(rev.total_ttc)}</span>
              </div>
            </div>
          </Card>

          {/* Factures fournisseurs */}
          <Card className="p-5 border-l-4 border-l-red-500" data-testid="card-expenses">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Factures fournisseurs</h3>
                <p className="text-xs text-slate-500">{exp.count} facture(s) en {year}</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total HT</span>
                <span className="font-medium">CHF {fmt(exp.total_ht)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">TVA deductible</span>
                <span className="font-medium text-red-600">CHF {fmt(exp.tva)}</span>
              </div>
              <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-2 mt-1">
                <span>Total TTC</span>
                <span className="text-red-700">CHF {fmt(exp.total_ttc)}</span>
              </div>
            </div>
          </Card>

          {/* Charges fixes */}
          <Card className="p-5 border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/general-expenses')} data-testid="card-charges-fixes">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Banknote className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Charges fixes</h3>
                <p className="text-xs text-slate-500">{gen?.count || 0} charge(s) en {year}</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-base font-bold">
                <span>Total</span>
                <span className="text-orange-700">CHF {fmt(gen?.total || 0)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-slate-200 pt-2 mt-1">
                <span className="text-slate-500">Total depenses</span>
                <span className="font-bold text-red-700">CHF {fmt(totalExpenses)}</span>
              </div>
              <div className={`flex justify-between text-sm font-bold ${rev.total_ttc - totalExpenses >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                <span>Resultat net</span>
                <span>CHF {fmt(rev.total_ttc - totalExpenses)}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* TVA Comparison + Objectif */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* TVA */}
          <Card className="p-5 border-l-4 border-l-amber-500" data-testid="card-tva">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <ArrowUpDown className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-sm font-bold text-slate-900">Solde TVA</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500">TVA collectee (factures etablies)</span>
                <span className="font-medium text-emerald-600">+ CHF {fmt(tva.collectee)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500">TVA deductible (factures fournisseurs)</span>
                <span className="font-medium text-red-600">- CHF {fmt(tva.deductible)}</span>
              </div>
              <div className="border-t-2 border-slate-300 pt-3 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-base font-bold text-slate-900">
                    {tva.solde >= 0 ? 'TVA a payer' : 'TVA a recuperer'}
                  </span>
                  <span className={`text-xl font-bold ${tva.solde >= 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    CHF {fmt(Math.abs(tva.solde))}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Objectif mensuel */}
          <Card className="p-5 border-l-4 border-l-indigo-500" data-testid="card-objectif">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Objectif mensuel</h3>
                  <p className="text-xs text-slate-500">{data.current_month_name} {year}</p>
                </div>
              </div>
              {!editingObj ? (
                <Button variant="ghost" size="sm" onClick={() => setEditingObj(true)} data-testid="edit-objectif-btn">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              ) : (
                <div className="flex items-center gap-1">
                  <Input type="number" value={objValue} onChange={e => setObjValue(e.target.value)}
                    className="w-28 h-8 text-sm" data-testid="input-objectif" />
                  <Button size="sm" variant="ghost" onClick={saveObjectif} className="text-emerald-600" data-testid="save-objectif-btn"><Check className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingObj(false)} className="text-slate-400"><X className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Objectif</span>
                <span className="font-medium">CHF {fmt(obj)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Realise ce mois</span>
                <span className="font-bold text-indigo-600">CHF {fmt(curRev)}</span>
              </div>
              {/* Progress bar */}
              <div>
                <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      objPct >= 100 ? 'bg-emerald-500' : objPct >= 75 ? 'bg-indigo-500' : objPct >= 50 ? 'bg-amber-500' : 'bg-red-400'
                    }`}
                    style={{ width: `${obj > 0 ? objPct : 0}%` }}
                    data-testid="objectif-progress"
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-slate-400">0</span>
                  <span className={`text-xs font-bold ${objPct >= 100 ? 'text-emerald-600' : 'text-slate-500'}`}>
                    {obj > 0 ? `${objPct.toFixed(0)}%` : 'Non defini'}
                  </span>
                  <span className="text-xs text-slate-400">{fmt(obj)}</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Monthly Comparison Chart */}
        <Card className="p-5" data-testid="monthly-comparison">
          <h3 className="text-sm font-bold text-slate-900 mb-1">Comparatif mensuel</h3>
          <div className="flex items-center gap-4 mb-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> Factures etablies</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 inline-block" /> Factures fournisseurs</span>
            {obj > 0 && <span className="flex items-center gap-1"><span className="w-6 border-t-2 border-dashed border-indigo-400 inline-block" /> Objectif</span>}
          </div>
          <div className="flex items-end gap-1.5 h-48 relative">
            {/* Objectif line */}
            {obj > 0 && (
              <div
                className="absolute left-0 right-0 border-t-2 border-dashed border-indigo-400 z-10"
                style={{ bottom: `${Math.min((obj / maxBar) * 100, 100)}%` }}
              >
                <span className="absolute -top-4 right-0 text-[10px] font-bold text-indigo-500">Obj: {(obj / 1000).toFixed(0)}k</span>
              </div>
            )}
            {monthly.map(m => {
              const totalCharges = m.total_charges || m.expenses.ttc;
              const rH = maxBar > 0 ? Math.max((m.revenue.ttc / maxBar) * 100, m.revenue.ttc > 0 ? 4 : 1) : 1;
              const eH = maxBar > 0 ? Math.max((totalCharges / maxBar) * 100, totalCharges > 0 ? 4 : 1) : 1;
              const isCurrent = m.month === data.current_month;
              return (
                <div key={m.month} className={`flex-1 flex flex-col items-center gap-0.5 ${isCurrent ? 'bg-indigo-50/50 rounded-lg py-1 -my-1' : ''}`}
                  data-testid={`month-${m.month}`}>
                  <div className="flex gap-0.5 items-end w-full justify-center" style={{ height: '160px' }}>
                    <div className="w-[45%] flex flex-col items-center justify-end h-full">
                      {m.revenue.ttc > 0 && <span className="text-[8px] font-bold text-emerald-700 mb-0.5">{(m.revenue.ttc / 1000).toFixed(1)}k</span>}
                      <div className="w-full bg-emerald-500 rounded-t" style={{ height: `${rH}%`, minHeight: m.revenue.ttc > 0 ? '4px' : '1px' }} />
                    </div>
                    <div className="w-[45%] flex flex-col items-center justify-end h-full">
                      {totalCharges > 0 && <span className="text-[8px] font-bold text-red-600 mb-0.5">{(totalCharges / 1000).toFixed(1)}k</span>}
                      <div className="w-full bg-red-400 rounded-t" style={{ height: `${eH}%`, minHeight: totalCharges > 0 ? '4px' : '1px' }} />
                    </div>
                  </div>
                  <span className={`text-[10px] ${isCurrent ? 'font-bold text-indigo-600' : 'text-slate-400'}`}>
                    {m.month_name.substring(0, 3)}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Monthly Table */}
        <Card className="overflow-hidden" data-testid="monthly-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="px-4 py-3 text-left font-medium">{t('common.month')}</th>
                  <th className="px-3 py-3 text-right font-medium text-emerald-300">Etablies HT</th>
                  <th className="px-3 py-3 text-right font-medium text-emerald-300">TVA col.</th>
                  <th className="px-3 py-3 text-right font-medium text-emerald-300">TTC</th>
                  <th className="px-3 py-3 text-right font-medium text-red-300">Fourn. TTC</th>
                  <th className="px-3 py-3 text-right font-medium text-orange-300">Charges fixes</th>
                  <th className="px-3 py-3 text-right font-medium text-red-300">Total depenses</th>
                  <th className="px-3 py-3 text-right font-medium text-amber-300">Solde TVA</th>
                  <th className="px-3 py-3 text-right font-medium">Resultat</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map((m, idx) => {
                  const hasData = m.revenue.count > 0 || m.expenses.count > 0 || (m.general_expenses || 0) > 0;
                  const soldeTva = m.revenue.tva - m.expenses.tva;
                  const totalCharges = m.total_charges || (m.expenses.ttc + (m.general_expenses || 0));
                  return (
                    <tr key={m.month} className={`border-b border-slate-100 ${idx % 2 ? 'bg-slate-50/50' : 'bg-white'}`}
                      data-testid={`row-month-${m.month}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-700">{m.month_name}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600">{hasData ? fmt(m.revenue.ht) : '-'}</td>
                      <td className="px-3 py-2.5 text-right text-emerald-600">{m.revenue.tva > 0 ? fmt(m.revenue.tva) : '-'}</td>
                      <td className="px-3 py-2.5 text-right font-medium text-emerald-700">{m.revenue.ttc > 0 ? fmt(m.revenue.ttc) : '-'}</td>
                      <td className="px-3 py-2.5 text-right font-medium text-red-700">{m.expenses.ttc > 0 ? fmt(m.expenses.ttc) : '-'}</td>
                      <td className="px-3 py-2.5 text-right font-medium text-orange-700">{(m.general_expenses || 0) > 0 ? fmt(m.general_expenses) : '-'}</td>
                      <td className="px-3 py-2.5 text-right font-bold text-red-700">{totalCharges > 0 ? fmt(totalCharges) : '-'}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${soldeTva >= 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {hasData ? `${soldeTva >= 0 ? '' : '-'}${fmt(Math.abs(soldeTva))}` : '-'}
                      </td>
                      <td className={`px-3 py-2.5 text-right font-bold ${m.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                        {hasData ? `${m.profit >= 0 ? '+' : ''}${fmt(m.profit)}` : '-'}
                      </td>
                    </tr>
                  );
                })}
                {/* Total row */}
                <tr className="bg-slate-800 text-white font-bold" data-testid="row-total">
                  <td className="px-4 py-3">TOTAL {year}</td>
                  <td className="px-3 py-3 text-right">{fmt(rev.total_ht)}</td>
                  <td className="px-3 py-3 text-right text-emerald-300">{fmt(rev.tva)}</td>
                  <td className="px-3 py-3 text-right text-emerald-300">{fmt(rev.total_ttc)}</td>
                  <td className="px-3 py-3 text-right text-red-300">{fmt(exp.total_ttc)}</td>
                  <td className="px-3 py-3 text-right text-orange-300">{fmt(gen?.total || 0)}</td>
                  <td className="px-3 py-3 text-right text-red-300">{fmt(totalExpenses)}</td>
                  <td className={`px-3 py-3 text-right ${tva.solde >= 0 ? 'text-amber-300' : 'text-emerald-300'}`}>{fmt(Math.abs(tva.solde))}</td>
                  <td className="px-3 py-3 text-right">{fmt(rev.total_ttc - totalExpenses)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default FinanceDashboard;
